import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { openFilePreview } from '@app/shared/utils/file-preview';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { InstallmentLoanService } from '@app/modules/installment-loans/services/installments-loans.service';
import { InstallmentsLoansContextService } from '@app/modules/installment-loans/services/installments-loans.context.service';
import { IInstallmentLoanStatementDTO } from '@app/shared/models/installment-loans.model';

@Component({
  selector: 'app-installment-loan-statement',
  templateUrl: './installment-loan-statement.component.html',
  styleUrls: ['./installment-loan-statement.component.sass'],
})
export class InstallmentLoanStatementComponent extends SubscriberComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;

  maxDate: Date | null = null;
  minDate: Date | null = null;
  availableStatements: IInstallmentLoanStatementDTO[] = [];
  rppUserId!: string;
  loanCode!: string;
  hasNotStatement = false;

  constructor(
    private readonly _installmentLoanSvc: InstallmentLoanService,
    private readonly _customerCtxt: CustomerContext,
    private readonly _installmentLoansCtxt: InstallmentsLoansContextService,
    private readonly _amplitudeSvc: AmplitudeService
  ) {
    super();
    this.rppUserId = this._customerCtxt.getRppUserId();
    this.loanCode = this._installmentLoansCtxt.getCurrentInstallmentLoan()?.code || '';
  }

  ngOnInit(): void {
    this.initForm();
    this.setStatementInfo();
    this.logAmplitudeEvent();
  }

  private initForm() {
    this.form = new FormGroup({
      date: new FormControl(null, [Validators.required]),
    });
  }

  get dateField() {
    return this.form.get('date') as FormControl;
  }

  showPreviewOnline() {
    this.isLoading = true;

    this._subscriptions.sink = this.getInstallmentLoanStatement().subscribe({
      next: res => {
        if (res?.file) {
          openFilePreview(res.file);
        }
        this.isLoading = false;
      },
      error: err => {
        console.error('Error al obtener el estado de cuenta:', err);
        this.isLoading = false;
      },
    });
  }

  private setStatementInfo() {
    if (!this.rppUserId || !this.loanCode) {
      this.dateField.disable();
      this.hasNotStatement = true;

      return;
    }

    this._subscriptions.sink = this._installmentLoanSvc.getInstallmentLoanStatementsInfo(this.rppUserId, this.loanCode).subscribe({
      next: data => {
        if (data && data.data && data.data.length > 0) {
          this.availableStatements = data.data;

          const cutOffDates = data.data.map(statement => moment(statement.cut_off_date));
          cutOffDates.sort((a, b) => a.valueOf() - b.valueOf());

          this.minDate = cutOffDates[0].endOf('month').toDate();
          this.maxDate = cutOffDates[cutOffDates.length - 1].endOf('month').toDate();
          this.dateField.setValue(this.maxDate);
        } else {
          this.dateField.disable();
          this.hasNotStatement = true;
        }
      },
      error: err => {
        console.error('Error al obtener información de estados de cuenta:', err);
        this.dateField.disable();
        this.hasNotStatement = true;
      },
    });
  }

  private getSelectedStatement(): IInstallmentLoanStatementDTO | undefined {
    const selectedDate = this.dateField.value;
    if (!selectedDate || !this.availableStatements.length) {
      return undefined;
    }

    const selectedMoment = moment(selectedDate);

    return this.availableStatements.find(statement => {
      const cutOffMoment = moment(statement.cut_off_date);

      return cutOffMoment.format('YYYY-MM') === selectedMoment.format('YYYY-MM');
    });
  }

  private getInstallmentLoanStatement() {
    const selectedStatement = this.getSelectedStatement();

    if (!selectedStatement) {
      throw new Error('No se encontró el estado de cuenta seleccionado');
    }

    const params = {
      rppUserId: this.rppUserId,
      loanCode: this.loanCode,
      statementCode: selectedStatement.code,
    };

    return this._installmentLoanSvc.getInstallmentLoanStatementPdf(params);
  }

  private logAmplitudeEvent() {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CONTRACT_STATEMENT,
    });
  }
}
